import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCall } from "@/api/commerce";
import { useTelephony, type CallRecord } from "@/api/ops";
import { Icon } from "@/components/icons";
import { Alert, Modal } from "@/components/ui";

export const OUTCOMES: [string, string][] = [
  ["connected", "Connected"], ["no_answer", "No answer"], ["busy", "Busy"], ["left_voicemail", "Left voicemail"],
  ["callback", "Asked to call back"], ["wrong_number", "Wrong number"],
];

const message = (e: unknown) => (e instanceof ApiError ? (e.problem.errors?.map((x) => x.message).join("; ") || e.message) : "Something went wrong");

function LogCallDialog({ contactId, dealId, name, onClose }: { contactId: string; dealId?: string; name: string; onClose: () => void }) {
  const call = useCall<CallRecord>("timeline", "calls", "activities");
  const [direction, setDirection] = useState<"out" | "in">("out");
  const [outcome, setOutcome] = useState("connected");
  const [minutes, setMinutes] = useState("5");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    try {
      await call.mutateAsync({ path: "/calls/log", body: { contact_id: contactId, deal_id: dealId ?? null, direction, outcome, duration_minutes: Number(minutes) || 0, notes: notes.trim() || null } });
      onClose();
    } catch (err) { setError(message(err)); }
  };
  return (
    <Modal title={`Log a call with ${name}`} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error && <Alert>{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-3">
          <div><label htmlFor="lc-dir" className="label">Direction</label>
            <select id="lc-dir" className="input" value={direction} onChange={(e) => setDirection(e.target.value as "out" | "in")}><option value="out">I called them</option><option value="in">They called me</option></select></div>
          <div><label htmlFor="lc-outcome" className="label">Outcome</label>
            <select id="lc-outcome" className="input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>{OUTCOMES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><label htmlFor="lc-min" className="label">Minutes</label><input id="lc-min" className="input" type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></div>
        </div>
        <div><label htmlFor="lc-notes" className="label">Notes</label><textarea id="lc-notes" className="input min-h-24" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was agreed, next steps" /></div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={call.isPending}>Save call</button></div>
      </form>
    </Modal>
  );
}

/** "Call" rings your phone through Exotel and then the customer; "Log call" records a call made any other way. */
export function CallButtons({ contactId, dealId, name, phone }: { contactId: string; dealId?: string; name: string; phone?: string | null }) {
  const telephony = useTelephony();
  const call = useCall<CallRecord>("calls", "timeline");
  const [logging, setLogging] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "danger"; text: string; profile?: boolean } | null>(null);
  const t = telephony.data;
  const dial = async () => {
    setNotice(null);
    try {
      await call.mutateAsync({ path: "/calls", body: { contact_id: contactId, deal_id: dealId ?? null } });
      setNotice({ tone: "ok", text: `Your phone (${t?.your_phone}) will ring now. Pick up and you are connected to ${name}. The call is logged when it ends.` });
    } catch (err) { setNotice({ tone: "danger", text: message(err), profile: err instanceof ApiError && err.problem.errors?.some((x) => x.field === "phone") }); }
  };
  return (
    <>
      {t?.click_to_call && phone !== null && <button type="button" className="btn-primary" disabled={call.isPending} onClick={() => void dial()} title={phone ? `Call ${phone}` : "Call"}><Icon name="call" />Call</button>}
      <button type="button" className="btn-ghost" onClick={() => setLogging(true)}><Icon name="call" />Log call</button>
      {notice && (
        <div className="fixed bottom-4 right-4 z-40 max-w-sm" role="status">
          <Alert tone={notice.tone}>{notice.text}{notice.profile && <> <Link className="underline" to="/settings/profile">Open My profile</Link></>}
            <button type="button" className="ml-2 text-xs underline" onClick={() => setNotice(null)}>Dismiss</button></Alert>
        </div>
      )}
      {logging && <LogCallDialog contactId={contactId} dealId={dealId} name={name} onClose={() => setLogging(false)} />}
    </>
  );
}
